import React, { createContext, useContext, useMemo } from 'react';
import { useHttpClientCore } from '@/shared';
import { ApiStorageRepository } from '../repositories/ApiStorageRepository';
import {
  DeleteStorageFileUseCase,
  DigitizeStorageFileUseCase,
  DownloadStorageFileUseCase,
  GetStorageFileAccessLogUseCase,
  GetUploadLocationsUseCase,
  ListStorageFilesUseCase,
  UploadStorageFileUseCase,
  ViewStorageFileUseCase,
} from '../../application/use-cases/StorageUseCases';

interface StorageDIContextType {
  repository: ApiStorageRepository;
  listStorageFilesUseCase: ListStorageFilesUseCase;
  uploadStorageFileUseCase: UploadStorageFileUseCase;
  deleteStorageFileUseCase: DeleteStorageFileUseCase;
  downloadStorageFileUseCase: DownloadStorageFileUseCase;
  digitizeStorageFileUseCase: DigitizeStorageFileUseCase;
  getStorageFileAccessLogUseCase: GetStorageFileAccessLogUseCase;
  viewStorageFileUseCase: ViewStorageFileUseCase;
  getUploadLocationsUseCase: GetUploadLocationsUseCase;
}

const StorageDIContext = createContext<StorageDIContextType | undefined>(undefined);

export const StorageDIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const httpClientCore = useHttpClientCore();

  const value = useMemo<StorageDIContextType>(() => {
    const repository = new ApiStorageRepository(httpClientCore);
    return {
      repository,
      listStorageFilesUseCase: new ListStorageFilesUseCase(repository),
      uploadStorageFileUseCase: new UploadStorageFileUseCase(repository),
      deleteStorageFileUseCase: new DeleteStorageFileUseCase(repository),
      downloadStorageFileUseCase: new DownloadStorageFileUseCase(repository),
      digitizeStorageFileUseCase: new DigitizeStorageFileUseCase(repository),
      getStorageFileAccessLogUseCase: new GetStorageFileAccessLogUseCase(repository),
      viewStorageFileUseCase: new ViewStorageFileUseCase(repository),
      getUploadLocationsUseCase: new GetUploadLocationsUseCase(repository),
    };
  }, [httpClientCore]);

  return <StorageDIContext.Provider value={value}>{children}</StorageDIContext.Provider>;
};

export const useStorageDIContext = (): StorageDIContextType => {
  const context = useContext(StorageDIContext);
  if (!context) {
    throw new Error('useStorageDIContext must be used within StorageDIProvider');
  }
  return context;
};
